import React, { useState, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MdAdd,
  MdBarChart,
  MdCampaign,
  MdContentCopy,
  MdDelete,
  MdEdit,
  MdQuestionAnswer,
  MdSend,
} from "react-icons/md";
import Card from "components/card";
import Badge from "components/badge/Badge";
import Button from "components/button";
import { PageContainer, PageHeader } from "components/page";
import StatCard from "components/stat-card";
import { SearchInput } from "components/fields";
import { SegmentedControl } from "components/tabs";
import Skeleton from "components/skeleton/Skeleton";
import { EmptyState, ErrorState } from "components/states";
import { useConfirm } from "components/dialog/ConfirmProvider";
import ActionMenu from "components/heartbeat/ActionMenu";
import CampaignStatusBadge from "components/heartbeat/CampaignStatusBadge";
import { getDeliveryMeta } from "components/heartbeat/deliveryMethod";
import heartbeatCampaignService from "services/heartbeatCampaignService";
import notify from "lib/notify";
import { getApiErrorMessage } from "api";
import { EMPTY, formatNumber, formatRelative } from "utils/format";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";

const SEGMENTS = [
  { value: "active", label: "Active" },
  { value: "draft", label: "Drafts" },
  { value: "paused", label: "Paused" },
  { value: "all", label: "All" },
];

const hasActivity = (campaign) => {
  const stats = campaign.stats || {};
  return Boolean(stats.sent || stats.scans || stats.responses);
};

// The API stores only `isActive`, so "inactive" covers both never-used drafts and paused
// campaigns. A campaign that has sent, been scanned or collected responses is paused.
const campaignState = (campaign) => {
  if (campaign.status === "active") return "active";
  if (campaign.status === "draft") return "draft";
  return hasActivity(campaign) ? "paused" : "draft";
};

// The list endpoint does not return a last-response timestamp yet; until it does, rows fall
// back to the latest date it does return.
const lastResponseAt = (campaign) => campaign.stats?.lastResponseAt || null;
const latestDate = (campaign) =>
  lastResponseAt(campaign) || campaign.updatedAt || campaign.createdAt || null;

const toTime = (value) => {
  const time = value ? Date.parse(value) : NaN;
  return Number.isNaN(time) ? 0 : time;
};

const hasResponses = (campaign) => (campaign.stats?.responses || 0) > 0;

// Most recent response first; campaigns without responses follow, newest first.
const byRecentResponse = (a, b) =>
  Number(hasResponses(b)) - Number(hasResponses(a)) ||
  toTime(latestDate(b)) - toTime(latestDate(a));

const ActivityText = ({ campaign }) => {
  if (!hasResponses(campaign)) return <span className="text-fg-muted">No responses yet</span>;
  const last = lastResponseAt(campaign);
  if (last) return <span className="text-fg">Last response {formatRelative(last)}</span>;
  const fallback = latestDate(campaign);
  return (
    <span className="text-fg">{fallback ? `Updated ${formatRelative(fallback)}` : EMPTY}</span>
  );
};

const CampaignRow = ({ campaign, onEdit, onDuplicate, onDelete }) => {
  const delivery = getDeliveryMeta(campaign.deliveryMethod);
  const DeliveryIcon = delivery.icon;
  const reach = delivery.isQr ? campaign.stats?.scans || 0 : campaign.stats?.sent || 0;
  const responses = campaign.stats?.responses || 0;
  const rate = reach > 0 ? `${Math.round((responses / reach) * 100)}%` : EMPTY;
  const rateLabel = `${formatNumber(responses)} of ${formatNumber(reach)} ${
    delivery.isQr ? "scans" : "responded"
  }`;

  const menuItems = [
    { id: "edit", label: "Edit", icon: MdEdit, onSelect: onEdit },
    { id: "duplicate", label: "Duplicate", icon: MdContentCopy, onSelect: onDuplicate },
    campaign.isSystem
      ? { id: "delete", label: "System Campaigns Can't Be Deleted", icon: MdDelete, disabled: true }
      : { id: "delete", label: "Delete", icon: MdDelete, tone: "danger", onSelect: onDelete },
  ];

  return (
    <Card
      padding="sm"
      className="relative transition-colors focus-within:bg-surface-subtle/40 hover:bg-surface-subtle/40"
    >
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex min-w-0 items-center gap-4">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-control bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200">
            <DeliveryIcon aria-hidden="true" className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="truncate text-base font-semibold text-fg">
                <Link
                  to={`/admin/heartbeat/${campaign.id}`}
                  className="focus-ring rounded-sm after:absolute after:inset-0 after:rounded-card after:content-['']"
                >
                  {campaign.name}
                </Link>
              </h2>
              <CampaignStatusBadge status={campaignState(campaign)} />
              {campaign.isSystem && <Badge tone="info">System</Badge>}
            </div>
            <p className="mt-0.5 text-sm text-fg-muted">
              {campaign.questions?.length || 0} questions ·{" "}
              <ActivityText campaign={campaign} />
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <p className="flex items-center gap-2 text-sm text-fg-muted">
            <DeliveryIcon aria-hidden="true" className="h-4 w-4" />
            {delivery.label}
          </p>

          <dl className="flex gap-5 text-sm">
            <div className="flex flex-col-reverse text-center">
              <dt className="text-xs text-fg-muted">{delivery.isQr ? "Scans" : "Sent"}</dt>
              <dd className="font-semibold text-fg">{formatNumber(reach)}</dd>
            </div>
            <div className="flex flex-col-reverse text-center">
              <dt className="text-xs text-fg-muted">Responses</dt>
              <dd className="font-semibold text-fg">{formatNumber(responses)}</dd>
            </div>
            <div className="flex flex-col-reverse text-center">
              <dt className="text-xs text-fg-muted">{rateLabel}</dt>
              <dd className="font-semibold text-fg">{rate}</dd>
            </div>
          </dl>

          <div className="relative z-10">
            <ActionMenu label={`Actions for ${campaign.name}`} items={menuItems} />
          </div>
        </div>
      </div>
    </Card>
  );
};

const GuestCampaignsList = () => {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [segment, setSegment] = useState("active");
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalSent: 0,
    totalResponses: 0,
  });

  useEffect(() => {
    track(EVENTS.HEARTBEAT_PAGE_VIEWED);
  }, []);

  useEffect(() => {
    fetchCampaigns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The list endpoint returns every campaign in one response, so search, segments and
  // ordering all happen here.
  const searched = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const matches = query
      ? campaigns.filter((c) => (c.name || "").toLowerCase().includes(query))
      : campaigns;
    return matches.map((c) => ({ campaign: c, state: campaignState(c) }));
  }, [campaigns, searchQuery]);

  const segmentOptions = useMemo(
    () =>
      SEGMENTS.map(({ value, label }) => {
        const count =
          value === "all" ? searched.length : searched.filter((r) => r.state === value).length;
        return {
          value,
          label: (
            <>
              {label} <span className="ml-1 tabular-nums text-fg-muted">{count}</span>
            </>
          ),
        };
      }),
    [searched]
  );

  const visibleCampaigns = useMemo(
    () =>
      searched
        .filter((r) => segment === "all" || r.state === segment)
        .map((r) => r.campaign)
        .sort(byRecentResponse),
    [searched, segment]
  );

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await heartbeatCampaignService.getCampaigns();
      if (response.success) {
        setCampaigns(response.data || []);

        const active = response.data.filter((c) => c.status === "active").length;
        const totalSent = response.data.reduce((sum, c) => sum + (c.stats?.sent || 0), 0);
        const totalResponses = response.data.reduce(
          (sum, c) => sum + (c.stats?.responses || 0),
          0
        );

        setStats({
          totalCampaigns: response.data.length,
          activeCampaigns: active,
          totalSent,
          totalResponses,
        });
      }
    } catch (err) {
      console.error("Error fetching campaigns:", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (campaign) => {
    try {
      await heartbeatCampaignService.duplicateCampaign(campaign.id);
      notify.success("Campaign duplicated");
      fetchCampaigns();
    } catch (err) {
      console.error("Error duplicating campaign:", err);
      notify.error(getApiErrorMessage(err, "Could not duplicate campaign"));
    }
  };

  const handleDelete = async (campaign) => {
    const confirmed = await confirm({
      title: `Delete "${campaign.name}"?`,
      message: "This campaign will be permanently deleted. This cannot be undone.",
      confirmLabel: "Delete",
      tone: "danger",
    });
    if (!confirmed) return;
    try {
      await heartbeatCampaignService.deleteCampaign(campaign.id);
      notify.success("Campaign deleted");
      fetchCampaigns();
    } catch (err) {
      console.error("Error deleting campaign:", err);
      notify.error(getApiErrorMessage(err, "Could not delete campaign"));
    }
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSegment("all");
  };
  const createCampaign = () => navigate("/admin/heartbeat/new");

  const renderList = () => {
    if (loading) {
      return (
        <div className="flex flex-col gap-3" aria-busy="true">
          {[0, 1, 2].map((key) => (
            <Skeleton key={key} className="h-20 w-full rounded-card" />
          ))}
        </div>
      );
    }
    if (error) {
      return (
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(error, "Campaigns could not be loaded.")}
            onRetry={fetchCampaigns}
          />
        </Card>
      );
    }
    if (campaigns.length > 0 && visibleCampaigns.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            variant="filtered"
            title="No campaigns match your filters"
            description="Try a different search or status."
            action={
              <Button variant="secondary" onClick={clearFilters}>
                Clear Filters
              </Button>
            }
          />
        </Card>
      );
    }
    if (campaigns.length === 0) {
      return (
        <Card padding="md">
          <EmptyState
            icon={MdCampaign}
            title="No Campaigns Yet"
            description="Create your first Heartbeat campaign to start collecting guest feedback."
            action={
              <Button leftIcon={<MdAdd aria-hidden="true" />} onClick={createCampaign}>
                Create Campaign
              </Button>
            }
          />
        </Card>
      );
    }
    return (
      <div className="flex flex-col gap-3">
        {visibleCampaigns.map((campaign) => (
          <CampaignRow
            key={campaign.id}
            campaign={campaign}
            onEdit={() => navigate(`/admin/heartbeat/${campaign.id}/edit`)}
            onDuplicate={() => handleDuplicate(campaign)}
            onDelete={() => handleDelete(campaign)}
          />
        ))}
      </div>
    );
  };

  return (
    <PageContainer>
      <PageHeader
        title="Campaigns"
        description="Create and manage first-time guest survey campaigns"
        actions={
          <Button leftIcon={<MdAdd aria-hidden="true" />} onClick={createCampaign}>
            Create Campaign
          </Button>
        }
      />

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          icon={MdBarChart}
          accent="sage"
          label="Total Campaigns"
          value={formatNumber(stats.totalCampaigns)}
          loading={loading}
        />
        <StatCard
          icon={MdCampaign}
          accent="sky"
          label="Active Campaigns"
          value={formatNumber(stats.activeCampaigns)}
          loading={loading}
        />
        <StatCard
          icon={MdSend}
          accent="slate"
          label="Surveys Sent"
          value={formatNumber(stats.totalSent)}
          loading={loading}
        />
        <StatCard
          icon={MdQuestionAnswer}
          accent="gold"
          label="Responses"
          value={formatNumber(stats.totalResponses)}
          loading={loading}
        />
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search campaigns by name…"
          ariaLabel="Search campaigns"
          className="w-full md:flex-1"
        />
        <SegmentedControl
          aria-label="Filter by status"
          value={segment}
          onChange={setSegment}
          options={segmentOptions}
          className="self-start md:self-auto"
        />
      </div>

      {renderList()}
    </PageContainer>
  );
};

export default GuestCampaignsList;
